/* eslint-disable */

import React, { useEffect, useState } from 'react';
import { Button, message, Modal, Spin, Table, Typography, Upload } from 'antd';
import { DownloadOutlined, InboxOutlined } from '@ant-design/icons';
import excel from 'assets/images/icons/excel.svg';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';

const { Dragger } = Upload;
const { Text } = Typography;

const ExcelUploadModal = ({ visible, onCancel, onApply, numberOfColumn = 3, totalRows = 0, startMonthStr = null }) => {
	const [excelData, setExcelData] = useState([]);
	const [parsedData, setParsedData] = useState([]);
	const [showData, setShowData] = useState(false);
	const [loading, setLoading] = useState(false);

	// Reset data when modal opens/closes
	useEffect(() => {
		if (!visible) {
			setExcelData([]);
			setParsedData([]);
			setShowData(false);
		}
	}, [visible]);

	// Download template
	const handleDownloadTemplate = () => {
		try {
			setLoading(true);

			// Create template data
			const startMonth = startMonthStr ? dayjs(startMonthStr, 'DD-MM-YYYY') : dayjs();
			const data = [];

			// Add header row
			if (numberOfColumn === 3) {
				data.push(['Month', 'Period', 'Gross Rent']);
			} else {
				data.push(['Month', 'Period', 'Advance Amount', 'Advance Adjustment']);
			}

			// Add data rows
			for (let i = 0; i < totalRows; i++) {
				const monthDate = startMonth.add(i, 'months');
				const formattedDate = monthDate.format('YYYY-MM-DD');

				if (numberOfColumn === 3) {
					data.push([formattedDate, i + 1, '']);
				} else {
					data.push([formattedDate, i + 1, '', '']);
				}
			}

			// Create workbook
			const wb = XLSX.utils.book_new();
			const ws = XLSX.utils.aoa_to_sheet(data);

			// Add worksheet to workbook
			XLSX.utils.book_append_sheet(wb, ws, numberOfColumn === 3 ? 'Gross Rent' : 'Advance Amount');

			// Generate file and download
			XLSX.writeFile(wb, numberOfColumn === 3 ? 'Gross_Rent_Template.xlsx' : 'Advance_Amount_Template.xlsx');

			message.success('Template downloaded successfully');
		} catch (error) {
			console.error('Error generating template:', error);
			message.error('Failed to generate template');
		} finally {
			setLoading(false);
		}
	};

	// Handle file upload
	const handleFileUpload = (file) => {
		setLoading(true);

		const reader = new FileReader();

		reader.onload = (e) => {
			try {
				// Process Excel file
				const arrayBuffer = e.target.result;

				// Parse Excel file
				const wb = XLSX.read(arrayBuffer, { type: 'array' });
				const sheetName = wb.SheetNames[0];
				const worksheet = wb.Sheets[sheetName];
				const rawData = XLSX.utils.sheet_to_json(worksheet);

				// Save the raw data for display
				setExcelData(rawData);

				// Format data for the form
				let formattedData;
				if (numberOfColumn === 3) {
					formattedData = rawData.map((row) => ({
						value: row['Gross Rent']?.toString() || '',
					}));
				} else {
					formattedData = rawData.map((row) => ({
						advanceAmount: row['Advance Amount']?.toString() || '',
						advanceAdjustment: row['Advance Adjustment']?.toString() || '',
					}));
				}

				// Store the formatted data for later use
				setParsedData(formattedData);

				// Show the data table
				setShowData(true);
				message.success(`${file.name} uploaded successfully`);
			} catch (error) {
				console.error('Error processing Excel file:', error);
				message.error(`Failed to process ${file.name}: ${error.message}`);
			} finally {
				setLoading(false);
			}
		};

		reader.onerror = () => {
			setLoading(false);
			message.error('Error reading file');
		};

		reader.readAsArrayBuffer(file);

		// Return false to prevent default upload
		return false;
	};

	// Apply the parsed data
	const handleApply = () => {
		if (!parsedData.length) {
			message.warning('No data to apply');
			return;
		}

		// Apply the formatted data to the main form
		onApply(parsedData);
		onCancel();
	};

	// Get columns for the table
	const getColumns = () => {
		const baseColumns = [
			{
				title: 'Month',
				dataIndex: 'Month',
				key: 'month',
				render: (text) => text || '-',
			},
			{
				title: 'Period',
				dataIndex: 'Period',
				key: 'period',
				render: (text) => text || '-',
			},
		];

		if (numberOfColumn === 3) {
			return [
				...baseColumns,
				{
					title: 'Gross Rent',
					dataIndex: 'Gross Rent',
					key: 'grossRent',
					render: (text) => text || '-',
				},
			];
		} else {
			return [
				...baseColumns,
				{
					title: 'Advance Amount',
					dataIndex: 'Advance Amount',
					key: 'advanceAmount',
					render: (text) => text || '-',
				},
				{
					title: 'Advance Adjustment',
					dataIndex: 'Advance Adjustment',
					key: 'advanceAdjustment',
					render: (text) => text || '-',
				},
			];
		}
	};

	return (
		<Modal
			title={numberOfColumn === 3 ? 'Import Gross Rent Data' : 'Import Advance Amount Data'}
			open={visible}
			onCancel={onCancel}
			width={800}
			footer={[
				<Button
					key="download"
					onClick={handleDownloadTemplate}
					icon={<DownloadOutlined />}
					disabled={!totalRows || totalRows <= 0 || !startMonthStr}
				>
					<img src={excel} alt="excel" width={16} style={{ marginRight: '5px' }} />
					Download Template
				</Button>,
				<Button key="cancel" onClick={onCancel}>
					Cancel
				</Button>,
				<Button key="apply" type="primary" onClick={handleApply} disabled={!showData || excelData.length === 0}>
					Apply
				</Button>,
			]}
		>
			<Spin spinning={loading}>
				{!showData ? (
					<div>
						<div style={{ marginBottom: '16px' }}>
							<Text>
								Upload an Excel file with {numberOfColumn === 3 ? 'gross rent' : 'advance amount'} data. You can download a template
								using the button below, fill it out, and upload.
							</Text>

							{(!totalRows || totalRows <= 0 || !startMonthStr) && (
								<div style={{ marginTop: '10px' }}>
									<Text type="warning">
										Please set Lease Term and Lease Start Date in the main form before downloading the template.
									</Text>
								</div>
							)}
						</div>

						<Dragger
							name="file"
							beforeUpload={(file) => handleFileUpload(file)}
							showUploadList={false}
							accept=".xlsx, .xls"
							disabled={loading}
						>
							<p className="ant-upload-drag-icon">
								<InboxOutlined />
							</p>
							<p className="ant-upload-text">Click or drag Excel file to this area to upload</p>
							<p className="ant-upload-hint">Support for .xlsx and .xls files</p>
						</Dragger>
					</div>
				) : (
					<div>
						<div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between' }}>
							<Text>Found {excelData.length} rows of data in the uploaded file.</Text>
							<Button
								onClick={() => {
									setExcelData([]);
									setParsedData([]);
									setShowData(false);
								}}
							>
								Upload Another File
							</Button>
						</div>

						<Table
							dataSource={excelData}
							columns={getColumns()}
							pagination={false}
							size="small"
							scroll={{ y: 300 }}
							rowKey={(record, index) => `${index}`}
						/>
					</div>
				)}
			</Spin>
		</Modal>
	);
};

export default ExcelUploadModal;
